/* MARCA | 07/05/2026 | Size Guide component – quiz calculator, tab management, and PDP variant selection */
import { DialogComponent } from '@theme/dialog';

/**
 * @typedef {object} VariantData
 * @property {number}  id
 * @property {string}  title
 * @property {string}  size
 * @property {number}  measurement
 * @property {boolean} available
 */

/**
 * @typedef {object} SizeGuideData
 * @property {string}        clothingType
 * @property {string}        sizeOptionName
 * @property {number}        sizeOptionIndex
 * @property {string}        measurementLabel
 * @property {VariantData[]} variants
 */

/**
 * @typedef {object} SizeGuideRefs
 * @property {HTMLDialogElement}   dialog
 * @property {HTMLElement}         [step1]
 * @property {HTMLElement}         [step2]
 * @property {HTMLInputElement}    [chestInput]
 * @property {HTMLInputElement[]}  [fitRadios]
 * @property {HTMLElement}         [resultText]
 * @property {HTMLElement}         [resultAlt]
 * @property {HTMLButtonElement}   [selectBtn]
 */

/**
 * Custom element powering the Size Guide modal.
 *
 * Extends DialogComponent to inherit show/close/toggle dialog behaviour,
 * then layers on tab-switching, a two-step "Find My Size" quiz, and the
 * ability to push the recommended size back into the PDP variant picker.
 *
 * @extends DialogComponent
 */
class SizeGuideComponent extends DialogComponent {
  /** @type {SizeGuideData | null} */
  #data = null;

  /** @type {VariantData[]} */
  #sortedVariants = [];

  /** @type {{ primary: VariantData; alternative: VariantData | null } | null} */
  #recommendation = null;

  connectedCallback() {
    super.connectedCallback();

    const jsonEl = this.querySelector("[data-size-guide-json]");
    if (!jsonEl?.textContent) return;

    this.#data = JSON.parse(jsonEl.textContent);
    this.#sortedVariants = [...(this.#data?.variants ?? [])]
      .filter((v) => v.measurement > 0)
      .sort((a, b) => a.measurement - b.measurement);
  }

  /* ───────────────────────── Dialog overrides ───────────────────────── */

  showDialog() {
    this.#resetQuiz();
    super.showDialog();
  }

  /* ──────────────────────────── Tab switching ─────────────────────────── */

  /** @param {Event} event */
  switchTab(event) {
    const tab = /** @type {HTMLElement | null} */ (event.target)?.closest(
      "[data-tab]",
    );
    if (!tab) return;

    const tabName = /** @type {HTMLElement} */ (tab).dataset.tab;

    for (const t of this.querySelectorAll(".size-guide__tab")) {
      if (!(t instanceof HTMLElement)) continue;
      const isActive = t.dataset.tab === tabName;
      t.classList.toggle("size-guide__tab--active", isActive);
      t.setAttribute("aria-selected", String(isActive));
    }

    for (const p of this.querySelectorAll(".size-guide__panel")) {
      if (!(p instanceof HTMLElement)) continue;
      p.classList.toggle(
        "size-guide__panel--active",
        p.dataset.panel === tabName,
      );
    }
  }

  /** Navigates the user from the info icon to the Measurements tab. */
  showMeasurementInfo() {
    const measTab = this.querySelector('[data-tab="measurements"]');
    if (measTab instanceof HTMLElement) measTab.click();
  }

  /* ───────────────────────── Quiz: step navigation ──────────────────── */

  #resetQuiz() {
    const { step1, step2, chestInput } = /** @type {SizeGuideRefs} */ (
      this.refs
    );
    step2?.classList.remove("size-guide__step--active");
    step1?.classList.add("size-guide__step--active");
    if (chestInput) {
      chestInput.classList.remove("size-guide__input--error");
    }
  }

  /** Returns to step 1 of the quiz. */
  goBack() {
    this.#resetQuiz();
  }

  /* ───────────────────────── Quiz: submit & calculate ────────────────── */

  handleSubmit() {
    const {
      chestInput,
      fitRadios,
      resultText,
      resultAlt,
      selectBtn,
      step1,
      step2,
    } = /** @type {SizeGuideRefs} */ (this.refs);

    if (!chestInput) return;

    const chestValue = parseFloat(chestInput.value);

    if (!chestValue || chestValue <= 0) {
      chestInput.classList.add("size-guide__input--error");
      chestInput.focus();
      return;
    }

    chestInput.classList.remove("size-guide__input--error");

    let selectedFit = "fitted";
    if (fitRadios) {
      for (const radio of fitRadios) {
        if (radio.checked) {
          selectedFit = radio.value;
          break;
        }
      }
    }

    const recommendation = this.#calculateSize(chestValue, selectedFit);

    this.#recommendation = recommendation;

    /* MARCA | 22/05/2026 | Show "no match" message when measurement is out of range */
    if (!recommendation) {
      if (resultText) {
        resultText.innerHTML =
          'Sorry, we don\u2019t have a size that matches your measurement. Please check the <button type="button" class="size-guide__inline-link" on:click="/showMeasurementInfo">measurements guide</button> for reference.';
      }
      if (resultAlt) resultAlt.style.display = "none";
      if (selectBtn) {
        selectBtn.dataset.sizeName = "";
        selectBtn.style.display = "none";
      }
      step1?.classList.remove("size-guide__step--active");
      step2?.classList.add("size-guide__step--active");
      return;
    }

    if (resultText) {
      resultText.innerHTML = `We recommend the right size for you is, <strong>${recommendation.primary.size}</strong>.`;
    }

    if (resultAlt) {
      if (recommendation.alternative) {
        const altLabel =
          selectedFit === "fitted" ? "a looser" : "a more fitted";
        resultAlt.innerHTML = `For ${altLabel} fit, try <strong>${recommendation.alternative.size}</strong>.`;
        resultAlt.style.display = "";
      } else {
        resultAlt.style.display = "none";
      }
    }

    if (selectBtn) {
      selectBtn.dataset.sizeName = recommendation.primary.size;
      selectBtn.style.display = "";
    }

    step1?.classList.remove("size-guide__step--active");
    step2?.classList.add("size-guide__step--active");
  }

  /**
   * Calculates the recommended size based on chest circumference and fit.
   *
   * A garment must be larger than the body to fit — the difference is called
   * "ease". Industry standard ease values (full chest circumference):
   *   fitted → +1–2″  (0.5–1″ per side / PTP)
   *   loose  → +3–4″  (1.5–2″ per side / PTP)
   *
   * Algorithm:
   *   1. Halve the chest circumference → body half-chest.
   *   2. Add ease per side based on fit to produce a target garment PTP.
   *   3. Find the variant whose garment PTP is closest to the target.
   *   4. Reject if the best match is still >2″ PTP away from the target,
   *      or if the garment PTP is smaller than the body half-chest (the
   *      shirt literally won't close).
   *   5. Offer the adjacent size in the opposite direction as an alternative.
   *
   * @param {number} chestMeasurement - Full chest circumference in inches.
   * @param {string} fit              - "fitted" or "loose".
   * @returns {{ primary: VariantData; alternative: VariantData | null } | null}
   */
  #calculateSize(chestMeasurement, fit) {
    if (this.#sortedVariants.length === 0) return null;

    const bodyHalfChest = chestMeasurement / 2;

    /* MARCA | 22/05/2026 | Ease values (per side) derived from industry sizing guides */
    const easePerSide = fit === "loose" ? 2 : 1;
    const targetPTP = bodyHalfChest + easePerSide;

    let closestIndex = 0;
    let minDiff = Infinity;

    for (let i = 0; i < this.#sortedVariants.length; i++) {
      const variant = this.#sortedVariants[i];
      if (!variant) continue;
      const diff = Math.abs(variant.measurement - targetPTP);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = i;
      }
    }

    const closest = this.#sortedVariants[closestIndex];
    if (!closest) return null;

    /* MARCA | 22/05/2026 | Reject if garment is smaller than body (won't fit)
       or if the closest match is more than 2″ PTP from the target */
    if (closest.measurement < bodyHalfChest || minDiff > 2) return null;

    const primary = closest;
    const lastIndex = this.#sortedVariants.length - 1;

    /** @type {VariantData | null} */
    let alternative = null;
    if (fit === "fitted" && closestIndex < lastIndex) {
      alternative = this.#sortedVariants[closestIndex + 1] ?? null;
    } else if (fit === "loose" && closestIndex > 0) {
      alternative = this.#sortedVariants[closestIndex - 1] ?? null;
    }

    if (alternative && alternative.id === primary.id) alternative = null;
    if (alternative && alternative.measurement < bodyHalfChest)
      alternative = null;

    return { primary, alternative };
  }

  /* ─────────────────── Variant selection on the PDP ──────────────────── */

  /** Pushes the recommended size into the PDP variant picker and closes the modal. */
  selectSize() {
    const sizeName = /** @type {SizeGuideRefs} */ (this.refs).selectBtn?.dataset
      .sizeName;
    if (!sizeName) return;

    this.#selectSizeOnPDP(sizeName);
    this.closeDialog();
  }

  /**
   * Finds the PDP variant picker and programmatically selects the radio / option
   * matching the given size value, triggering the theme's built-in variant update flow.
   *
   * @param {string} sizeValue - The display value of the size (e.g. "M", "Large").
   */
  #selectSizeOnPDP(sizeValue) {
    const variantPicker = document.querySelector(
      'variant-picker[data-template-product-match="true"]',
    );
    if (!variantPicker || !this.#data) return;

    const sizeOptionIndex = this.#data.sizeOptionIndex;

    const fieldsets = variantPicker.querySelectorAll(
      'fieldset[ref="fieldsets[]"]',
    );
    const sizeFieldset = fieldsets[sizeOptionIndex];

    if (sizeFieldset) {
      const radios = sizeFieldset.querySelectorAll('input[type="radio"]');
      for (const radio of radios) {
        if (radio instanceof HTMLInputElement && radio.value === sizeValue) {
          radio.click();
          return;
        }
      }
    }

    const selects = variantPicker.querySelectorAll("select");
    if (selects[sizeOptionIndex]) {
      const select = selects[sizeOptionIndex];
      for (const opt of select.options) {
        if (opt.value === sizeValue || opt.textContent?.trim() === sizeValue) {
          select.value = opt.value;
          select.dispatchEvent(new Event("change", { bubbles: true }));
          return;
        }
      }
    }
  }
}

/* MARCA | 07/05/2026 | Register the size-guide-component custom element */
if (!customElements.get('size-guide-component')) {
  customElements.define('size-guide-component', SizeGuideComponent);
}
